import React, { useCallback, useState } from "react";
import { TextButton } from "@graffer-inc/console-design-kit";
import { BaseModal } from "../../atoms/base-modal";
import classnames from "classnames";
import { matchType } from "../../../application/domain/base/base-type";
import {
  GroupBatchImportResult,
  toErrorMessage,
} from "../../../application/domain/group/group-batch-import-result";
import { CsvFileType, CsvUploader } from "../../molecules/csv-uploader";

type BatchType = "IMPORT" | "UPDATE";
type OperationState = "SELECT" | "PREVIEW" | "RESULT";

export const BatchImportGroupModal: React.FC<{
  isActive: boolean;
  isBatchImporting: boolean;
  orgId: string;
  batchType?: BatchType;
  onClose: () => void;
  onImport: (
    organizationId: string,
    csv: string,
    validateOnly: boolean,
  ) => Promise<GroupBatchImportResult | undefined>;
  onDownloadTemplateCsv?: () => void;
}> = ({
  isActive,
  isBatchImporting,
  orgId,
  batchType = "IMPORT",
  onClose,
  onImport,
  onDownloadTemplateCsv,
}) => {
  const [pageStatus, setPageState] = useState<OperationState>("SELECT");
  const [file, setFile] = useState<CsvFileType | undefined>();
  const [result, setResult] = useState<GroupBatchImportResult>();
  const operationLabel = batchType === "IMPORT" ? "登録" : "更新";
  const canExecImport = result?.validateResult.isValid;
  const errorsCount = result?.validateResult.errors.length ?? 0;
  const previewMessage =
    errorsCount === 0
      ? `取込内容を確認し、一括${operationLabel}を実行する場合は実行ボタンを押下してください。`
      : canExecImport
        ? `${errorsCount}件の警告があります。取込内容を確認し、一括${operationLabel}を実行する場合は実行ボタンを押下してください。`
        : `${errorsCount}件のエラーがあります。CSVファイルを修正して再度アップロードしてください。`;

  const execPreview = useCallback(async () => {
    if (!file) {
      return;
    }

    const res = await onImport(orgId, file.data, true);
    if (res) {
      setResult(res);
      setPageState("PREVIEW");
    }
  }, [onImport, orgId, file]);
  const execImport = useCallback(async () => {
    if (!file) {
      return;
    }

    const res = await onImport(orgId, file.data, false);
    if (res) {
      if (res.count > 0) setPageState("RESULT");
      setResult(res);
    }
  }, [onImport, orgId, file]);
  const handleClose = useCallback(() => {
    setPageState("SELECT");
    setFile(undefined);
    onClose();
  }, [setPageState, setFile, onClose]);

  return (
    <BaseModal
      title={matchType(pageStatus, {
        SELECT: `グループ一括${operationLabel}`,
        PREVIEW: "一括登録内容の確認",
        RESULT: `グループ一括${operationLabel}`,
      })}
      isActive={isActive}
      onClose={handleClose}
    >
      {matchType(pageStatus, {
        SELECT: (
          <>
            <section className="modal-card-body">
              <div className="mb-4">
                {batchType === "IMPORT" && onDownloadTemplateCsv ? (
                  <div>
                    <p>
                      テンプレートCSVをダウンロードし、グループ名称を記入してアップロードしてください。
                    </p>
                    <TextButton
                      icon={"DownloadIcon"}
                      text="CSVをダウンロードする"
                      onClick={onDownloadTemplateCsv}
                    />
                  </div>
                ) : (
                  <p>
                    グループ一覧にてCSV出力を行い、グループ名称を編集してアップロードしてください。
                  </p>
                )}
              </div>
              <CsvUploader file={file} setFile={setFile} />
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={handleClose}
              >
                キャンセル
              </button>
              <button
                className={classnames("button is-info is-fullwidth")}
                onClick={execPreview}
                disabled={!file || isBatchImporting}
              >
                内容の確認に進む
              </button>
            </footer>
          </>
        ),
        PREVIEW: (
          <>
            {result && (
              <>
                <section className="modal-card-body">
                  <div className="mb-4">
                    <p>{previewMessage}</p>
                  </div>
                  <table className="table is-fullwidth is-bordered">
                    <thead>
                      <tr>
                        {batchType === "UPDATE" && <th>グループID</th>}
                        <th>グループ名称</th>
                        <th>警告/エラー</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result?.validateResult.csvRows.map((r, i) => {
                        const error = result.validateResult.errors.find(
                          (e) => e.errIdx === i,
                        );
                        return (
                          <tr
                            key={r.id}
                            className={classnames({
                              "has-background-warning-light":
                                error?.severity === "warn",
                              "has-background-danger-light":
                                error?.severity === "error",
                            })}
                          >
                            {batchType === "UPDATE" && <td>{r.id}</td>}
                            <td>{r.name}</td>
                            <td>
                              {error && <p>{toErrorMessage(error.errCode)}</p>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </section>
                <footer className="modal-card-foot">
                  <button
                    className="button is-failure is-fullwidth"
                    onClick={() => setPageState("SELECT")}
                  >
                    戻る
                  </button>
                  <button
                    className={classnames("button is-info is-fullwidth")}
                    onClick={execImport}
                    disabled={!file || isBatchImporting || !canExecImport}
                  >
                    一括{operationLabel}を実行する
                  </button>
                </footer>
              </>
            )}
          </>
        ),
        RESULT: (
          <>
            <section className="modal-card-body">
              {batchType === "IMPORT" ? (
                <>{result?.count}件の一括登録が完了しました</>
              ) : (
                <>
                  {result?.validateResult.csvRows.length}
                  件の一括更新を実行し、差分があった{result?.count}
                  件のグループを更新しました。
                </>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                className={classnames("button is-fullwidth")}
                onClick={handleClose}
                disabled={false}
              >
                閉じる
              </button>
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};
